import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Check, Flag } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DateFilterField } from "@/components/date-filter-field";
import { ExportSignInsForm } from "@/components/export-signins-form";
import SignInActivityItem from "@/components/sign-in-activity-item";
import { SignInStatusDot } from "@/components/signin-status-dot";
import { ListSkeleton } from "@/components/skeletons";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Text } from "@/components/ui/typography";
import {
  groupSignInsByDate,
  type SignInGroup,
  type SignInRecord,
} from "@/lib/signins";
import { queryClient, trpc } from "@/utils/trpc";

type SiteSignInsSectionProps = {
  siteId: string;
  siteName?: string | null;
};

export function SiteSignInsSection({
  siteId,
  siteName,
}: SiteSignInsSectionProps) {
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [workerId, setWorkerId] = useState("all");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  const normalizedStartDate = useMemo(
    () => (startDate ? format(startDate, "yyyy-MM-dd") : undefined),
    [startDate]
  );
  const normalizedEndDate = useMemo(
    () => (endDate ? format(endDate, "yyyy-MM-dd") : undefined),
    [endDate]
  );
  const resolvedWorkerId = workerId === "all" ? undefined : workerId;

  const signins = useQuery({
    ...trpc.getSiteSignIns.queryOptions({
      siteId,
      workerId: resolvedWorkerId,
      startDate: normalizedStartDate,
      endDate: normalizedEndDate,
    }),
    enabled: Boolean(siteId),
  });

  const workerOptions = signins.data?.workers ?? [];
  const records = signins.data?.records ?? [];
  const isFilterApplied = workerId !== "all" || !!startDate || !!endDate;

  const handleClearFilters = () => {
    setWorkerId("all");
    setStartDate(undefined);
    setEndDate(undefined);
  };

  return (
    <>
      <Card className="gap-0 overflow-hidden py-0">
        <CardHeader className="px-6 pt-6 pb-4">
          <CardTitle>Worker sign-ins</CardTitle>
          <CardDescription>
            {siteName
              ? `Recent sign-in activity for ${siteName}.`
              : "Recent sign-in activity for this site."}
          </CardDescription>
          <CardAction>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExportOpen(true)}
            >
              Export CSV
            </Button>
          </CardAction>
        </CardHeader>

        <div className="flex flex-wrap items-center gap-3 border-t border-border px-6 py-4">
          <Select value={workerId} onValueChange={setWorkerId}>
            <SelectTrigger
              id="site-worker-filter"
              aria-label="Filter by worker"
              className="h-10 w-full sm:w-52"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All workers</SelectItem>
              {workerOptions.map((worker) => (
                <SelectItem key={worker.id} value={worker.id}>
                  {worker.fullName ?? worker.email ?? worker.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DateFilterField
            id="site-log-start-date"
            label="Start date"
            placeholder="Start date"
            date={startDate}
            onChange={setStartDate}
            hideLabel
          />
          <DateFilterField
            id="site-log-end-date"
            label="End date"
            placeholder="End date"
            date={endDate}
            onChange={setEndDate}
            hideLabel
          />
          {isFilterApplied ? (
            <Button variant="ghost" size="sm" onClick={handleClearFilters}>
              Reset
            </Button>
          ) : null}
        </div>

        <SignInContent
          isLoading={signins.isLoading}
          error={signins.error as Error | null}
          records={records}
          isFilterApplied={isFilterApplied}
          onClearFilters={handleClearFilters}
          onRetry={() => void signins.refetch()}
        />
      </Card>

      <Drawer open={isExportOpen} onOpenChange={setIsExportOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Export sign-ins</DrawerTitle>
            <DrawerDescription>
              Export sign-in activity for this site with the current filters.
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-4">
            <ExportSignInsForm
              allowSiteSelection={false}
              siteId={siteId}
              workerId={resolvedWorkerId}
              defaultStartDate={startDate}
              defaultEndDate={endDate}
              onExportComplete={() => setIsExportOpen(false)}
              className="gap-4"
            />
          </div>
          <hr />
          <DrawerFooter>
            <DrawerClose asChild>
              <Button variant="outline">Close</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}

export function SignInContent({
  isLoading,
  error,
  records,
  isFilterApplied,
  onClearFilters,
  onRetry,
}: {
  isLoading: boolean;
  error: Error | null;
  records: SignInRecord[];
  isFilterApplied: boolean;
  onClearFilters: () => void;
  onRetry: () => void;
}) {
  if (isLoading) {
    return (
      <div className="border-t border-border px-6 py-6">
        <ListSkeleton rows={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-t border-border px-6 py-6">
        <Alert variant="destructive">
          <AlertTitle>Couldn’t load sign-ins</AlertTitle>
          <AlertDescription>
            {error.message || "Check your connection and try again."}
          </AlertDescription>
          <AlertAction>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Try again
            </Button>
          </AlertAction>
        </Alert>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="border-t border-border px-6 py-8">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>
              {isFilterApplied ? "No matching sign-ins" : "No sign-ins yet"}
            </EmptyTitle>
            <EmptyDescription>
              {isFilterApplied
                ? "Try changing or resetting the current filters."
                : "Sign-ins appear here once workers scan in at this site."}
            </EmptyDescription>
          </EmptyHeader>
          {isFilterApplied ? (
            <Button variant="outline" size="sm" onClick={onClearFilters}>
              Reset filters
            </Button>
          ) : null}
        </Empty>
      </div>
    );
  }

  const groups = groupSignInsByDate(records);

  return (
    <>
      <div className="border-t border-border px-6 py-4 md:hidden">
        <SignInList groups={groups} />
      </div>
      <div className="hidden overflow-x-auto border-t border-border md:block">
        <SignInTable records={records} />
      </div>
    </>
  );
}

const SIGN_IN_DATE_FORMAT = "d MMM · HH:mm";

function formatMethod(method: string | null | undefined): string {
  if (!method) return "—";
  if (method.toLowerCase() === "qr") return "QR";
  return method.charAt(0).toUpperCase() + method.slice(1).toLowerCase();
}

function SignInTableRow({ record }: { record: SignInRecord }) {
  const updateFlagged = useMutation({
    ...trpc.updateSignInFlagged.mutationOptions(),
    onSuccess: () => {
      toast.success("Flag status updated");
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : null;
      toast.error(message || "Failed to update flag status");
    },
  });

  return (
    <TableRow>
      <TableCell className="w-8 pl-6 pr-0">
        <SignInStatusDot online={!record.signOutTime} />
      </TableCell>
      <TableCell className="font-medium capitalize">
        {record.userId ? (
          <Link
            to={`/workers/${record.userId}/sign-ins`}
            className="hover:underline"
          >
            {record.workerName}
          </Link>
        ) : (
          record.workerName
        )}
      </TableCell>
      <TableCell className="capitalize text-muted-foreground">
        {record.jobTitle ?? "—"}
      </TableCell>
      <TableCell className="font-meta text-muted-foreground">
        {format(new Date(record.signInTime), SIGN_IN_DATE_FORMAT)}
      </TableCell>
      <TableCell className="font-meta text-muted-foreground">
        {record.signOutTime ? (
          format(new Date(record.signOutTime), SIGN_IN_DATE_FORMAT)
        ) : (
          <span className="font-body italic">Active</span>
        )}
      </TableCell>
      <TableCell className="font-meta text-muted-foreground">
        {formatMethod(record.method)}
      </TableCell>
      <TableCell className="pr-0 text-right">
        {record.flagged ? (
          <Badge variant="warning" size="sm" className="gap-1">
            <Flag className="h-3 w-3" aria-hidden="true" />
            Flagged
          </Badge>
        ) : null}
      </TableCell>
      <TableCell className="w-10 pl-2 pr-6 text-right">
        {record.flagged ? (
          <Button
            size="icon-xs"
            variant="ghost"
            className="-my-1"
            title="Mark reviewed"
            aria-label="Mark reviewed"
            onClick={() =>
              updateFlagged.mutate({ signInId: record.id, flagged: false })
            }
            disabled={updateFlagged.isPending}
          >
            <Check aria-hidden="true" />
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function SignInTable({ records }: { records: SignInRecord[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8 pl-6 pr-0" />
          <TableHead>Worker</TableHead>
          <TableHead>Job title</TableHead>
          <TableHead>Signed in</TableHead>
          <TableHead>Signed out</TableHead>
          <TableHead>Method</TableHead>
          <TableHead className="pr-0 text-right">Status</TableHead>
          <TableHead className="w-10 pr-6" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {records.map((record) => (
          <SignInTableRow key={record.id} record={record} />
        ))}
      </TableBody>
    </Table>
  );
}

function SignInList({ groups }: { groups: SignInGroup[] }) {
  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <SignInGroupView key={group.ts} group={group} />
      ))}
    </div>
  );
}

function SignInGroupView({ group }: { group: SignInGroup }) {
  return (
    <div className="space-y-2">
      <Text variant="label" tone="muted" as="p">
        {group.label}
      </Text>
      <ul className="divide-y rounded-md border bg-card">
        {group.items.map((record) => (
          <SignInListItem key={record.id} record={record} />
        ))}
      </ul>
    </div>
  );
}

function SignInListItem({ record }: { record: SignInRecord }) {
  const updateFlagged = useMutation({
    ...trpc.updateSignInFlagged.mutationOptions(),
    onSuccess: () => {
      toast.success("Flag status updated");
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : null;
      toast.error(message || "Failed to update flag status");
    },
  });

  return (
    <li className="p-4">
      <SignInActivityItem
        title={record.workerName}
        subtitle={record.jobTitle ?? undefined}
        signInTime={record.signInTime}
        signOutTime={record.signOutTime}
        method={record.method}
        flagged={record.flagged}
        distanceFromSite={record.distanceFromSite ?? undefined}
        withinGeofence={record.withinGeofence ?? undefined}
      />
      {record.flagged ? (
        <div className="mt-3 flex justify-end">
          <Button
            size="sm"
            onClick={() =>
              updateFlagged.mutate({ signInId: record.id, flagged: false })
            }
            disabled={updateFlagged.isPending}
            className="w-full"
          >
            {updateFlagged.isPending ? "Updating…" : "Mark as reviewed"}
          </Button>
        </div>
      ) : null}
    </li>
  );
}
